import React, {useEffect, useRef, useState} from 'react';
import paper from 'paper';

const ShaderVectorizer = ({ captureData }) => {
    const canvasRef = useRef(null);
    const previewCanvasRef = useRef(null);
    const shaderPreviewRef = useRef(null);

    const [cvReady, setCvReady] = useState(false);
    const [ckReady, setCkReady] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    // Shader Settings
    const [targetHue, setTargetHue] = useState(0.05);       // Normalized 0.0 - 1.0
    const [hueTolerance, setHueTolerance] = useState(0.05); // How far the hue can drift
    const [minSaturation, setMinSaturation] = useState(0.2);// Ignore gray/white pixels
    const [minValue, setMinValue] = useState(0.2);          // Ignore black/dark shadows

    // Vectorizer Settings
    const [minBlobArea, setMinBlobArea] = useState(200);

    // 1. Load WebAssembly Libraries (OpenCV & CanvasKit)
    useEffect(() => {
        paper.setup(canvasRef.current);

        // Load OpenCV
        if (!window.cv) {
            const scriptCv = document.createElement("script");
            scriptCv.src = "https://docs.opencv.org/4.8.0/opencv.js";
            scriptCv.async = true;
            scriptCv.onload = () => {
                if (window.cv instanceof Promise) window.cv.then((target) => { window.cv = target; setCvReady(true); });
                else if (window.cv.getBuildInformation) setCvReady(true);
                else window.cv['onRuntimeInitialized'] = () => setCvReady(true);
            };
            document.body.appendChild(scriptCv);
        } else {
            setCvReady(true);
        }

        // Load CanvasKit (Skia)
        if (!window.CanvasKit) {
            const scriptCk = document.createElement("script");
            scriptCk.src = "https://unpkg.com/canvaskit-wasm@latest/bin/canvaskit.js";
            scriptCk.async = true;
            scriptCk.onload = () => {
                window.CanvasKitInit({
                    locateFile: (file) => 'https://unpkg.com/canvaskit-wasm@latest/bin/' + file
                }).then((ck) => {
                    window.CanvasKit = ck;
                    setCkReady(true);
                });
            };
            document.body.appendChild(scriptCk);
        } else {
            setCkReady(true);
        }

        return () => paper.project.clear();
    }, []);

    // 2. Draw Original Image
    useEffect(() => {
        if (!captureData || !previewCanvasRef.current) return;
        const { canvas: sourceCanvas } = captureData;
        const pCanvas = previewCanvasRef.current;

        // Scale down slightly for performance while testing shaders
        const scale = sourceCanvas.width / 400;
        pCanvas.width = 400;
        pCanvas.height = Math.max(1, Math.floor(sourceCanvas.height / scale));

        const ctx = pCanvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(sourceCanvas, 0, 0, pCanvas.width, pCanvas.height);
    }, [captureData]);

    // Helper: Standard RGB to HSV converter
    const rgbToHsv = (r, g, b) => {
        r /= 255; g /= 255; b /= 255;
        let max = Math.max(r, g, b), min = Math.min(r, g, b);
        let h, s, v = max;
        let d = max - min;
        s = max === 0 ? 0 : d / max;
        if (max === min) h = 0;
        else {
            switch (max) {
                case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                case g: h = (b - r) / d + 2; break;
                case b: h = (r - g) / d + 4; break;
                default: break;
            }
            h /= 6;
        }
        return { h, s, v };
    };

    // 3. Extract exact Skin Hue using MediaPipe Mask
    const extractSkinHue = () => {
        if (!captureData) return;
        const pCanvas = previewCanvasRef.current;
        const ctx = pCanvas.getContext('2d');
        const imgData = ctx.getImageData(0, 0, pCanvas.width, pCanvas.height).data;

        const origMask = captureData.mask;
        const maskScale = captureData.width / pCanvas.width;

        let sumSin = 0;
        let sumCos = 0;
        let count = 0;

        for (let y = 0; y < pCanvas.height; y++) {
            for (let x = 0; x < pCanvas.width; x++) {
                const origX = Math.floor(x * maskScale);
                const origY = Math.floor(y * maskScale);
                const category = origMask[origY * captureData.width + origX];

                // Category 2 & 3 are Body and Face Skin
                if (category === 2 || category === 3) {
                    const idx = (y * pCanvas.width + x) * 4;
                    const { h, s, v } = rgbToHsv(imgData[idx], imgData[idx+1], imgData[idx+2]);

                    // Ignore dark shadows or highlights when calculating base hue
                    if (s > 0.15 && v > 0.2 && v < 0.95) {
                        // Hue is circular (0-1), so we convert to radians and sum vectors to average properly
                        const angle = h * 2 * Math.PI;
                        sumSin += Math.sin(angle);
                        sumCos += Math.cos(angle);
                        count++;
                    }
                }
            }
        }

        if (count > 0) {
            let avgAngle = Math.atan2(sumSin, sumCos);
            if (avgAngle < 0) avgAngle += 2 * Math.PI;
            const avgHue = avgAngle / (2 * Math.PI);

            setTargetHue(avgHue);
            console.log(`Extracted Base Skin Hue: ${avgHue.toFixed(3)} from ${count} pixels`);
        } else {
            alert("No skin detected in the MediaPipe mask.");
        }
    };


    // 4. Skia Shader + OpenCV Vectorization Pipeline
    const runPipeline = async () => {
        if (!cvReady || !ckReady || !captureData) return;
        setIsProcessing(true);
        paper.project.clear();

        const ck = window.CanvasKit;
        const cv = window.cv;
        const pCanvas = previewCanvasRef.current;
        const width = pCanvas.width;
        const height = pCanvas.height;

        // --- A. CANVASKIT SHADER EXECUTION ---
        const surface = ck.MakeSurface(width, height);
        if (!surface) {
            console.error("Failed to create CanvasKit surface.");
            setIsProcessing(false);
            return;
        }
        const canvas = surface.getCanvas();

        const ckImage = ck.MakeImageFromCanvasImageSource(pCanvas);
        const imageShader = ckImage.makeShaderCubic(ck.TileMode.Clamp, ck.TileMode.Clamp, 1/3, 1/3);

        const sksl = `
      uniform shader image;
      uniform float targetHue;
      uniform float hueTolerance;
      uniform float minSaturation;
      uniform float minValue;
      
      vec3 rgb2hsv(vec3 c) {
          vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
          vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
          vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));
          float d = q.x - min(q.w, q.y);
          float e = 1.0e-10;
          return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
      }

      vec4 main(vec2 coord) {
          vec4 color = image.eval(coord);
          vec3 hsv = rgb2hsv(color.rgb);
          
          float dist = abs(hsv.x - targetHue);
          if (dist > 0.5) dist = 1.0 - dist;
          
          if (dist <= hueTolerance && hsv.y >= minSaturation && hsv.z >= minValue) {
              return vec4(1.0, 1.0, 1.0, 1.0); 
          }
          return vec4(0.0, 0.0, 0.0, 1.0); 
      }
    `;

        const effect = ck.RuntimeEffect.Make(sksl);
        const uniforms = new Float32Array([targetHue, hueTolerance, minSaturation, minValue]);
        const shader = effect.makeShaderWithChildren(uniforms, [imageShader]);

        const paint = new ck.Paint();
        paint.setShader(shader);
        canvas.drawRect(ck.LTRBRect(0, 0, width, height), paint);

        surface.flush();
        const snapshot = surface.makeImageSnapshot();

        const imageInfo = {
            width: width, height: height,
            colorType: ck.ColorType.RGBA_8888,
            alphaType: ck.AlphaType.Unpremul,
            colorSpace: ck.ColorSpace.SRGB
        };

        let pixels = null;
        try { pixels = snapshot.readPixels(0, 0, imageInfo); }
        catch (err) { pixels = snapshot.readPixels(imageInfo, 0, 0); }

        if (!pixels) {
            setIsProcessing(false);
            return;
        }

        const imgData = new ImageData(new Uint8ClampedArray(pixels), width, height);

        const shaderCanvas = shaderPreviewRef.current;
        shaderCanvas.width = width;
        shaderCanvas.height = height;
        shaderCanvas.getContext('2d').putImageData(imgData, 0, 0);

        snapshot.delete(); surface.delete(); ckImage.delete();
        imageShader.delete(); paint.delete(); effect.delete(); shader.delete();

        // --- B. OPENCV CONTOUR VECTORIZATION (WITH HOLES) ---

        let cvMat = cv.imread(shaderCanvas);
        let grayMat = new cv.Mat();
        cv.cvtColor(cvMat, grayMat, cv.COLOR_RGBA2GRAY, 0);

        let contours = new cv.MatVector();
        let hierarchy = new cv.Mat();

        // ⭐ CHANGE 1: cv.RETR_CCOMP organizes contours into Outer Boundaries and Inner Holes
        cv.findContours(grayMat, contours, hierarchy, cv.RETR_CCOMP, cv.CHAIN_APPROX_NONE);

        const raster = new paper.Raster(pCanvas);
        const scaleToPaper = Math.max(paper.view.bounds.width / width, paper.view.bounds.height / height);
        raster.scale(scaleToPaper);
        raster.position = paper.view.center;
        raster.opacity = 0.35;

        const offsetX = paper.view.center.x - (width * scaleToPaper) / 2;
        const offsetY = paper.view.center.y - (height * scaleToPaper) / 2;

        let pathLayer = new paper.Layer({ name: 'SkinBlobs' });
        pathLayer.activate();

        // OpenCV Hierarchy Array gives us 4 values per contour: [Next, Previous, First_Child, Parent]
        let hierarchyData = hierarchy.data32S;

        // Helper to convert an OpenCV contour to a raw Paper.js Path
        const createRawPath = (contourObj) => {
            let path = new paper.Path({ closed: true });
            let pointsArray = contourObj.data32S;
            for (let p = 0; p < pointsArray.length; p += 2) {
                const mapX = offsetX + (pointsArray[p] * scaleToPaper);
                const mapY = offsetY + (pointsArray[p+1] * scaleToPaper);
                path.add(new paper.Point(mapX, mapY));
            }
            path.simplify(1.5);
            return path;
        };

        for (let i = 0; i < contours.size(); ++i) {
            // Index 3 holds the Parent ID. If Parent is -1, this is an OUTER boundary.
            let parentIdx = hierarchyData[i * 4 + 3];

            if (parentIdx === -1) {
                let outerContour = contours.get(i);

                if (cv.contourArea(outerContour) >= minBlobArea) {

                    // ⭐ CHANGE 2: Create a CompoundPath to hold the boundary + holes
                    let compoundPath = new paper.CompoundPath({
                        strokeColor: '#00e5ff',
                        strokeWidth: 2,
                        fillColor: 'rgba(0, 229, 255, 0.4)',
                        fillRule: 'evenodd' // This tells Paper.js to cut holes out!
                    });

                    // 1. Add the main outer boundary
                    let outerPath = createRawPath(outerContour);
                    compoundPath.addChild(outerPath);

                    // 2. Find any inner holes (Index 2 holds the First_Child ID)
                    let childIdx = hierarchyData[i * 4 + 2];

                    // Iterate through all children (holes) belonging to this outer boundary
                    while (childIdx !== -1) {
                        let holeContour = contours.get(childIdx);

                        // Optional: Ignore microscopic noise holes
                        if (cv.contourArea(holeContour) >= 10) {
                            let holePath = createRawPath(holeContour);
                            compoundPath.addChild(holePath);
                        }
                        holeContour.delete();

                        // Move to the next sibling hole (Index 0 holds the Next ID)
                        childIdx = hierarchyData[childIdx * 4 + 0];
                    }
                }
                outerContour.delete();
            }
            // If parentIdx !== -1, we do nothing in the main loop because we already processed it as a hole above!
        }

        paper.view.update();

        cvMat.delete(); grayMat.delete(); contours.delete(); hierarchy.delete();
        setIsProcessing(false);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', fontFamily: 'sans-serif' }}>
            <h3>4. Skia Shader (CanvasKit) + OpenCV Pipeline</h3>

            {(!cvReady || !ckReady) && (
                <div style={{ padding: '10px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '6px' }}>
                    ⏳ Loading WebAssembly Engines (OpenCV & Skia)...
                </div>
            )}

            <div style={{ display: 'flex', gap: '20px' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '15px' }}>

                    <div style={{ display: 'flex', gap: '10px' }}>
                        <div style={{ flex: 1, backgroundColor: '#333', padding: '10px', borderRadius: '8px', textAlign: 'center' }}>
                            <p style={{ color: '#aaa', margin: '0 0 10px 0', fontSize: '11px' }}>Raw Image</p>
                            <canvas ref={previewCanvasRef} style={{ width: '100%', backgroundColor: '#000', borderRadius: '4px' }} />
                        </div>

                        <div style={{ flex: 1, backgroundColor: '#333', padding: '10px', borderRadius: '8px', textAlign: 'center' }}>
                            <p style={{ color: '#aaa', margin: '0 0 10px 0', fontSize: '11px' }}>Skia Shader Output</p>
                            <canvas ref={shaderPreviewRef} style={{ width: '100%', backgroundColor: '#000', borderRadius: '4px' }} />
                        </div>
                    </div>

                    <div style={{ padding: '15px', backgroundColor: '#f0f0f0', borderRadius: '8px' }}>

                        <button onClick={extractSkinHue} disabled={!captureData} style={{ width: '100%', padding: '10px', marginBottom: '15px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: 'pointer' }}>
                            1. Auto-Extract Skin Hue (MediaPipe)
                        </button>

                        <label style={{ display: 'block', marginBottom: '15px', fontWeight: 'bold' }}>
                            Target Hue: {targetHue.toFixed(3)}
                            <input type="range" min="0" max="1" step="0.01" value={targetHue} onChange={(e) => setTargetHue(parseFloat(e.target.value))} style={{ width: '100%' }} />
                        </label>

                        <label style={{ display: 'block', marginBottom: '15px', fontWeight: 'bold' }}>
                            Hue Tolerance: {hueTolerance.toFixed(3)}
                            <input type="range" min="0.01" max="0.3" step="0.01" value={hueTolerance} onChange={(e) => setHueTolerance(parseFloat(e.target.value))} style={{ width: '100%' }} />
                        </label>

                        <label style={{ display: 'block', marginBottom: '15px', fontWeight: 'bold' }}>
                            Min Saturation: {minSaturation.toFixed(2)}
                            <input type="range" min="0" max="1" step="0.05" value={minSaturation} onChange={(e) => setMinSaturation(parseFloat(e.target.value))} style={{ width: '100%' }} />
                        </label>

                        <label style={{ display: 'block', marginBottom: '15px', fontWeight: 'bold' }}>
                            Min Value (Brightness): {minValue.toFixed(2)}
                            <input type="range" min="0" max="1" step="0.05" value={minValue} onChange={(e) => setMinValue(parseFloat(e.target.value))} style={{ width: '100%' }} />
                        </label>

                        <label style={{ display: 'block', fontWeight: 'bold' }}>Min Blob Area: {minBlobArea}px
                            <input type="range" min="50" max="2000" step="50" value={minBlobArea} onChange={(e) => setMinBlobArea(parseInt(e.target.value))} style={{ width: '100%' }} />
                        </label>
                    </div>

                    <button onClick={runPipeline} disabled={isProcessing || !captureData || !cvReady || !ckReady} style={{ padding: '12px 20px', backgroundColor: (isProcessing || !cvReady || !ckReady) ? '#888' : '#e91e63', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>
                        {isProcessing ? 'Processing Shader...' : '✨ 2. Run Shader & Vectorize'}
                    </button>
                </div>

                <div style={{ flex: 2 }}>
                    <canvas ref={canvasRef} width={800} height={600} style={{ backgroundColor: '#222', borderRadius: '12px', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)', width: '100%', height: 'auto' }} />
                </div>
            </div>
        </div>
    );
};

export default ShaderVectorizer;